// src/hooks/useWizard.js
import { useState, useCallback, useRef } from 'react';

export const useWizard = (steps, initialData = {}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [validSteps, setValidSteps] = useState(new Set([1]));
  const [completedSteps, setCompletedSteps] = useState([]);
  const [data, setData] = useState(initialData);
  const isProcessingRef = useRef(false);

  const updateData = useCallback((newData) => {
    setData(prevData => ({ ...prevData, ...newData }));
  }, []);

  const validateStep = useCallback((stepNumber, validationFn, customData = null) => {
    // Usa i dati custom se forniti, altrimenti usa data dell'hook
    const dataToValidate = customData || data;
    const isValid = validationFn ? validationFn(dataToValidate) : true;
    
    if (isValid) {
      setValidSteps(prev => {
        const newSet = new Set([...prev, stepNumber + 1]);
        return newSet;
      });
      if (!completedSteps.includes(stepNumber)) {
        setCompletedSteps(prev => {
          const newCompleted = [...prev, stepNumber];
          return newCompleted;
        });
      }
    }
    
    return isValid;
  }, [data, completedSteps]);

  const invalidateStepsAfter = useCallback((stepNumber) => {
    setValidSteps(prev => {
      const newValidSteps = new Set();
      // Mantieni solo gli step fino a stepNumber (incluso)
      for (let i = 1; i <= stepNumber; i++) {
        newValidSteps.add(i);
      }
      return newValidSteps;
    });
    
    setCompletedSteps(prev => prev.filter(step => step <= stepNumber));
    
    // Se lo step corrente è maggiore di stepNumber, torna a stepNumber
    if (currentStep > stepNumber) {
      setCurrentStep(stepNumber);
    }
  }, [currentStep]);

  const goToStep = useCallback((stepNumber, validationFunctions = {}) => {
    // Per andare allo step X, tutti gli step precedenti devono essere validi
    if (stepNumber === 1) {
      // Lo step 1 è sempre accessibile
      setCurrentStep(stepNumber);
      return;
    }
    
    // Controlla che tutti gli step precedenti siano validi
    let canNavigate = true;
    for (let i = 1; i < stepNumber; i++) {
      if (validationFunctions[i]) {
        const isValid = validationFunctions[i](data);
        if (!isValid) {
          canNavigate = false;
          // Rimuovi gli step non validi da validSteps
          setValidSteps(prev => {
            const newSet = new Set(prev);
            for (let j = i + 1; j <= 4; j++) {
              newSet.delete(j);
            }
            return newSet;
          });
          break;
        }
      }
    }
    
    if (canNavigate && validSteps.has(stepNumber)) {
      setCurrentStep(stepNumber);
    }
  }, [validSteps, data]);

  const nextStep = useCallback(() => {
    const nextStepNumber = currentStep + 1;
    setCurrentStep(nextStepNumber);
  }, [currentStep]);

  // Nuova funzione che combina validazione e navigazione
  const validateAndProceed = useCallback((stepNumber, validationFn, customData = null) => {
    // Previeni doppi click
    if (isProcessingRef.current) {
      return false;
    }
    
    isProcessingRef.current = true;
    
    const dataToValidate = customData || data;
    const isValid = validationFn ? validationFn(dataToValidate) : true;
    
    if (isValid) {
      // Usa setState con funzioni per evitare closure stale
      setValidSteps(prev => {
        const newSet = new Set(prev);
        newSet.add(stepNumber + 1);
        return newSet;
      });
      
      // Aggiorna completedSteps se necessario
      setCompletedSteps(prev => {
        if (!prev.includes(stepNumber)) {
          return [...prev, stepNumber];
        }
        return prev;
      });
      
      // Naviga al prossimo step
      setCurrentStep(stepNumber + 1);
      
      // Reset il flag dopo un breve delay per assicurarsi che gli stati siano aggiornati
      setTimeout(() => {
        isProcessingRef.current = false;
      }, 100);
      
      return true;
    }
    
    isProcessingRef.current = false;
    return false;
  }, [data]);

  const prevStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const navigateToOptimalStep = useCallback(() => {
    // Trova il primo step non completato
    const maxStep = Math.max(...steps.map(s => s.id));
    for (let i = 1; i <= maxStep; i++) {
      if (!completedSteps.includes(i)) {
        goToStep(i);
        break;
      }
    }
  }, [steps, completedSteps, goToStep]);

  return {
    currentStep,
    validSteps,
    completedSteps,
    data,
    setCurrentStep,
    setValidSteps,
    setCompletedSteps,
    updateData,
    validateStep,
    validateAndProceed,
    invalidateStepsAfter,
    goToStep,
    nextStep,
    prevStep,
    navigateToOptimalStep
  };
};